'use client';

import { useState } from 'react';
import { cn } from '@/app/lib/utils';

interface BlogCoverImageProps {
  src: string;
  alt: string;
  title?: string;
  className?: string;
}

function CoverPlaceholder({ title }: { title?: string }) {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#506C83]/15 to-[#181A1D]/10 dark:from-[#506C83]/25 dark:to-[#181A1D]/20"
      aria-hidden
    >
      <span className="text-[#506C83] text-6xl font-bold opacity-20 dark:opacity-30">
        {title?.[0]?.toUpperCase() || 'B'}
      </span>
    </div>
  );
}

/**
 * Blog post cover image with error fallback.
 * Uses native <img> so onError fires when Supabase returns 500 or the file is missing.
 * Shows gradient + first letter placeholder instead of broken image icon.
 */
export function BlogCoverImage({ src, alt, title, className }: BlogCoverImageProps) {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (error) {
    return <CoverPlaceholder title={title || alt} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={cn(
        'absolute inset-0 h-full w-full object-cover transition-opacity duration-300',
        loaded ? 'opacity-100' : 'opacity-0',
        className
      )}
      onLoad={() => setLoaded(true)}
      onError={() => setError(true)}
      loading="eager"
      decoding="async"
      fetchPriority="high"
    />
  );
}
